import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toolbar } from '@components/Toolbar';
import { FilterBar } from '@components/FilterBar';
import { CameraGrid } from '@components/CameraGrid';
import { StatePanel } from '@components/StatePanel';
import { CameraFormDrawer } from '@components/CameraFormDrawer';
import { CameraSettingsDrawer } from '@components/CameraSettingsDrawer';
import { CameraCardContextMenu } from '@components/CameraCardContextMenu';
import { GroupsManagerDialog } from '@components/GroupsManagerDialog';
import { ConfirmDialog } from '@components/ConfirmDialog';
import { useCameras } from '@hooks/useCameras';
import { useCameraGroups } from '@hooks/useCameraGroups';
import { useDebounced } from '@hooks/useDebounced';
import { camerasApi } from '@api/cameras';
import { HttpError } from '@api/http';
import { buildCameraQuery, useDashboardStore } from '@store/dashboardStore';
import type { Camera } from '@api/types';
import { useHasPermission } from '@auth/guards';

const PAGE_SIZE = 48;

type FormState = { mode: 'closed' } | { mode: 'create' };

export default function Dashboard(): JSX.Element {
  const navigate = useNavigate();
  const { filters, layout } = useDashboardStore();
  const debouncedSearch = useDebounced(filters.search, 250);

  const query = useMemo(
    () => ({
      ...buildCameraQuery({ ...filters, search: debouncedSearch }),
      page: 1,
      pageSize: PAGE_SIZE,
    }),
    [filters, debouncedSearch],
  );

  const { data, isLoading, error, refresh, setOptimistic } = useCameras(query);
  const {
    groups,
    isLoading: groupsLoading,
    refresh: refreshGroups,
  } = useCameraGroups();

  const [formState, setFormState] = useState<FormState>({ mode: 'closed' });
  const [settingsCamera, setSettingsCamera] = useState<Camera | null>(null);
  const [cardContextMenu, setCardContextMenu] = useState<{
    camera: Camera;
    x: number;
    y: number;
  } | null>(null);
  const [groupsDialogOpen, setGroupsDialogOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Camera | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const canCameraCrud = useHasPermission('camera_crud');
  const canCameraGroupCrud = canCameraCrud;

  const handleToggleFavorite = useCallback(
    async (camera: Camera) => {
      const next = !camera.isFavorite;
      setOptimistic((prev) => ({
        ...prev,
        items: prev.items.map((c) => (c.id === camera.id ? { ...c, isFavorite: next } : c)),
      }));
      try {
        await camerasApi.setFavorite(camera.id, next);
      } catch {
        refresh();
      }
    },
    [refresh, setOptimistic],
  );

  const handleToggleStatus = useCallback(
    async (camera: Camera) => {
      const next = camera.status === 'active' ? 'inactive' : 'active';
      setOptimistic((prev) => ({
        ...prev,
        items: prev.items.map((c) => (c.id === camera.id ? { ...c, status: next } : c)),
      }));
      try {
        await camerasApi.setStatus(camera.id, next);
      } catch {
        refresh();
      }
    },
    [refresh, setOptimistic],
  );

  const handleSaved = useCallback(() => {
    setFormState({ mode: 'closed' });
    refresh();
  }, [refresh]);

  const handleSettingsSaved = useCallback((_saved: Camera) => {
    setSettingsCamera(null);
    refresh();
  }, [refresh]);

  const handleCardContextMenu = useCallback((camera: Camera, e: MouseEvent<HTMLElement>) => {
    setCardContextMenu({ camera, x: e.clientX, y: e.clientY });
  }, []);

  const handleDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await camerasApi.remove(pendingDelete.id);
      setPendingDelete(null);
      refresh();
    } catch (err) {
      const message =
        err instanceof HttpError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to delete camera';
      setDeleteError(message);
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, refresh]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="flex h-full min-h-0 flex-col bg-bg text-foreground">
      <Toolbar
        totalCount={total}
        visibleCount={items.length}
        onAddCamera={() => canCameraCrud && setFormState({ mode: 'create' })}
        onManageGroups={() => canCameraGroupCrud && setGroupsDialogOpen(true)}
        canManageCameras={canCameraCrud}
        canManageGroups={canCameraGroupCrud}
      />
      <FilterBar />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {error ? (
          <StatePanel
            variant="error"
            title="Couldn’t load cameras"
            description={error}
            action={
              <button
                type="button"
                onClick={refresh}
                className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-foreground hover:border-accent"
              >
                Retry
              </button>
            }
          />
        ) : isLoading && items.length === 0 ? (
          <StatePanel
            title="Loading cameras…"
            description="Fetching the live grid from the backend."
          />
        ) : items.length === 0 ? (
          <StatePanel
            variant="empty"
            title="No cameras yet"
            description="Add your first camera to start streaming."
            action={
              canCameraCrud ? (
                <button
                  type="button"
                  onClick={() => setFormState({ mode: 'create' })}
                  className="rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-white hover:bg-accent-hover"
                >
                  + Add camera
                </button>
              ) : null
            }
          />
        ) : (
          <CameraGrid
            cameras={items}
            layout={layout}
            onToggleFavorite={canCameraCrud ? handleToggleFavorite : () => undefined}
            onToggleStatus={canCameraCrud ? handleToggleStatus : () => undefined}
            onOpenSettings={canCameraCrud ? (camera) => setSettingsCamera(camera) : () => undefined}
            onOpenPlayback={(camera) => navigate(`/cameras/${camera.id}/playback`)}
            onDelete={canCameraCrud ? (camera) => {
              setPendingDelete(camera);
              setDeleteError(null);
            } : () => undefined}
            onContextMenu={canCameraCrud ? handleCardContextMenu : () => undefined}
            canManageCameras={canCameraCrud}
          />
        )}
      </div>

      <CameraFormDrawer
        open={formState.mode === 'create'}
        groups={groups}
        onClose={() => setFormState({ mode: 'closed' })}
        onSaved={handleSaved}
      />

      <CameraSettingsDrawer
        open={settingsCamera !== null}
        camera={settingsCamera}
        groups={groups}
        onClose={() => setSettingsCamera(null)}
        onSaved={handleSettingsSaved}
      />

      <CameraCardContextMenu
        camera={cardContextMenu?.camera ?? null}
        position={cardContextMenu ? { x: cardContextMenu.x, y: cardContextMenu.y } : null}
        onClose={() => setCardContextMenu(null)}
        onSettings={(camera) => setSettingsCamera(camera)}
        onDelete={(camera) => {
          setCardContextMenu(null);
          setPendingDelete(camera);
          setDeleteError(null);
        }}
      />

      <GroupsManagerDialog
        open={groupsDialogOpen && canCameraGroupCrud}
        groups={groups}
        groupsLoading={groupsLoading}
        onClose={() => {
          setGroupsDialogOpen(false);
          refresh();
        }}
        onChanged={refreshGroups}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete) && canCameraCrud}
        title="Delete camera?"
        message={
          pendingDelete
            ? `"${pendingDelete.name}" will be removed from the dashboard and unregistered from MediaMTX. Recordings on disk are not deleted.`
            : ''
        }
        confirmLabel="Delete"
        busy={deleting}
        errorMessage={deleteError}
        onCancel={() => {
          if (!deleting) {
            setPendingDelete(null);
            setDeleteError(null);
          }
        }}
        onConfirm={handleDelete}
      />
    </div>
  );
}
